import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  BarChart3,
  Building2,
  Check,
  ChevronDown,
  FileText,
  HandCoins,
  Search,
  Package,
  Settings,
  Shield,
  UserCheck,
  Zap
} from 'lucide-react';

const getDirectoryIcon = (iconType, className = 'h-4 w-4') => {
  switch (iconType) {
    case 'BarChart3':
      return <BarChart3 className={className} />;
    case 'FileText':
      return <FileText className={className} />;
    case 'Users':
      return <UserCheck className={className} />;
    case 'Settings':
      return <Settings className={className} />;
    case 'ClipboardList':
      return <Building2 className={className} />;
    case 'Package':
      return <Package className={className} />;
    case 'Shield':
      return <Shield className={className} />;
    case 'HandCoins':
      return <HandCoins className={className} />;
    case 'Zap':
      return <Zap className={className} />;
    default:
      return <BarChart3 className={className} />;
  }
};

const useDismissableLayer = (isOpen, onClose, refs) => {
  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event) => {
      const target = event.target;
      const clickedInside = refs.some((ref) => ref.current && ref.current.contains(target));
      if (!clickedInside) {
        onClose();
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose, refs]);
};

const HeaderDirectorySwitcher = ({
  directories = [],
  currentDirectory = null,
  isGlobal = false,
  onSelectDirectory,
  mobile = false
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const rootRef = useRef(null);
  const searchInputRef = useRef(null);

  useDismissableLayer(open, () => setOpen(false), [rootRef]);

  const filteredDirectories = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return directories;
    return directories.filter((directory) => directory.label.toLowerCase().includes(query));
  }, [directories, search]);

  useEffect(() => {
    if (!open) {
      setSearch('');
      setHighlightedIndex(-1);
      return;
    }

    const id = requestAnimationFrame(() => {
      searchInputRef.current?.focus();
    });

    return () => cancelAnimationFrame(id);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (filteredDirectories.length === 0) {
      setHighlightedIndex(-1);
      return;
    }

    const currentIndex = filteredDirectories.findIndex((directory) => directory.path === currentDirectory?.path);
    setHighlightedIndex(currentIndex >= 0 ? currentIndex : 0);
  }, [open, filteredDirectories, currentDirectory]);

  if (!directories.length) return null;

  const hasMultipleDirectories = directories.length > 1;
  const neutralLabel = 'Selecionar diretoria';
  const displayLabel = isGlobal ? neutralLabel : currentDirectory?.label || neutralLabel;
  const currentIcon = isGlobal ? null : currentDirectory?.icon;
  const triggerLabel = hasMultipleDirectories
    ? isGlobal
      ? 'Selecionar diretoria'
      : `Diretoria atual: ${currentDirectory?.label || neutralLabel}`
    : directories[0]?.label || neutralLabel;

  const handleSelect = (directory) => {
    onSelectDirectory?.(directory);
    setOpen(false);
    setSearch('');
  };

  const handleTriggerKeyDown = (event) => {
    if (!hasMultipleDirectories) return;
    if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setOpen(true);
    }
  };

  const handleListKeyDown = (event) => {
    if (!open || filteredDirectories.length === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlightedIndex((current) => (current + 1) % filteredDirectories.length);
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightedIndex((current) => (current <= 0 ? filteredDirectories.length - 1 : current - 1));
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      const directory = filteredDirectories[highlightedIndex];
      if (directory) {
        handleSelect(directory);
      }
    }
  };

  const wrapperClassName = mobile
    ? 'relative w-full'
    : 'relative w-full lg:w-[240px] lg:max-w-[240px]';

  const triggerClassName = hasMultipleDirectories
    ? `group flex h-[40px] w-full items-center justify-between gap-2 rounded-xl border bg-transparent px-2.5 text-left text-[color:var(--text-primary)] transition-all duration-200 hover:bg-[color:var(--header-hover-soft)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)] active:scale-[0.995] ${
        open ? 'border-[color:var(--accent)]/35' : 'border-[color:var(--header-control-border-subtle)]'
      } ${mobile ? '' : 'opacity-[0.88]'}`
    : `flex h-[40px] w-full items-center gap-2 rounded-xl border border-[color:var(--header-control-border-subtle)] bg-[color:var(--surface)] px-2.5 text-left text-[color:var(--text-primary)] ${
        mobile ? '' : 'opacity-90'
      }`;

  const triggerContent = (
    <span className="flex min-w-0 items-center gap-2">
      <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border border-[color:var(--header-border)] bg-[color:var(--surface-muted)] text-[color:var(--text-primary)]">
        {currentIcon ? getDirectoryIcon(currentIcon) : <BarChart3 className="h-4 w-4 opacity-70" />}
      </span>
      <span className="min-w-0">
        <span className="block truncate text-sm font-medium leading-tight">{displayLabel}</span>
      </span>
    </span>
  );

  return (
    <div ref={rootRef} className={wrapperClassName}>
      {hasMultipleDirectories ? (
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          onKeyDown={handleTriggerKeyDown}
          className={triggerClassName}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={triggerLabel}
        >
          {triggerContent}
          <ChevronDown
            className={`h-4 w-4 shrink-0 text-[color:var(--text-muted)] transition-transform group-hover:text-[color:var(--text-primary)] ${
              open ? 'rotate-180' : ''
            }`}
          />
        </button>
      ) : (
        <div className={triggerClassName} aria-label={triggerLabel}>
          {triggerContent}
        </div>
      )}

      {hasMultipleDirectories && open && (
        <div
          className="absolute left-0 right-0 z-[70] mt-2 origin-top animate-[header-pop_180ms_ease-out] rounded-3xl border border-[color:var(--border)] bg-[color:var(--surface)] p-2 text-[color:var(--text-primary)] shadow-[0_20px_50px_rgba(0,0,0,0.18)] lg:right-auto lg:w-[320px]"
          onKeyDown={handleListKeyDown}
        >
          <div className="sticky top-0 z-10 rounded-2xl bg-[color:var(--surface)] px-2 pb-2 pt-1">
            <div className="flex items-center justify-between gap-3 px-1 pb-2">
              <p className="text-sm font-semibold text-[color:var(--text-primary)]">
                Diretorias disponiveis ({directories.length})
              </p>
            </div>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[color:var(--text-muted)]" />
              <input
                ref={searchInputRef}
                className="min-h-[44px] w-full rounded-xl border border-[color:var(--border)] bg-[color:var(--surface-muted)] py-2 pl-10 pr-3 text-sm text-[color:var(--text-primary)] placeholder:text-[color:var(--text-muted)] outline-none focus:ring-2 focus:ring-[color:var(--accent)]"
                placeholder="Buscar diretoria..."
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>
          </div>

          <div className="relative">
            <ul className="app-scroll-subtle max-h-72 overflow-auto pr-1" role="listbox" aria-label="Diretorias disponíveis">
              {filteredDirectories.map((directory, index) => {
                const isSelected = currentDirectory?.path === directory.path;
                const isHighlighted = highlightedIndex === index;

                return (
                  <li key={directory.path} role="option" aria-selected={isSelected}>
                    <button
                      type="button"
                      className={`relative flex min-h-[50px] w-full items-center gap-3 rounded-2xl px-3 py-2.5 text-left text-sm transition ${
                        isSelected
                          ? 'bg-[color:var(--surface-muted)] text-[color:var(--text-primary)] shadow-[inset_0_0_0_1px_var(--header-border)]'
                          : isHighlighted
                            ? 'bg-[color:var(--header-hover-soft)] text-[color:var(--text-primary)]'
                            : 'text-[color:var(--text-primary)] hover:bg-[color:var(--surface-muted)]'
                      }`}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      onClick={() => handleSelect(directory)}
                    >
                      <span
                        className={`absolute bottom-2 left-0 top-2 w-1 rounded-r-full ${
                          isSelected ? 'bg-[color:var(--accent)]' : 'bg-transparent'
                        }`}
                        aria-hidden="true"
                      />
                      <span
                        className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border bg-[color:var(--surface)] ${
                          isSelected
                            ? 'border-[color:var(--accent)]/35 text-[color:var(--accent)]'
                            : 'border-[color:var(--header-border)] text-[color:var(--text-primary)]'
                        }`}
                      >
                        {getDirectoryIcon(directory.icon)}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-medium">{directory.label}</span>
                      </span>
                      {isSelected && (
                        <span className="ml-1 inline-flex h-5 w-5 items-center justify-center rounded-full border border-[color:var(--header-border)] bg-[color:var(--surface)]">
                          <Check className="h-3.5 w-3.5 text-[color:var(--accent)]" />
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
              {filteredDirectories.length === 0 && (
                <li className="px-3 py-4 text-center text-xs text-[color:var(--text-muted)]">Nenhuma diretoria encontrada</li>
              )}
            </ul>
            <div className="pointer-events-none absolute inset-x-1 bottom-0 h-5 rounded-b-2xl bg-gradient-to-t from-[color:var(--surface)] to-transparent" />
          </div>
        </div>
      )}
    </div>
  );
};

export default HeaderDirectorySwitcher;
